import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import { serviceFaqs } from "@/data/serviceFaqs";
import img from "@/assets/voice-control.jpg";

const bullets = [
  "Integration with all major voice assistants",
  "Privacy-first local processing options for sensitive rooms",
  "Custom voice scenes — 'Movie time,' 'Goodnight,' 'Dinner party'",
  "Multi-room microphone coverage with whole-home audio",
  "Voice control for lighting, shades, HVAC, locks, and cameras",
  "Tuned wake-word sensitivity to avoid false triggers",
];

export const Route = createFileRoute("/services/voice-control")({
  head: () => ({
    meta: [
      { title: "Voice Control & Smart Assistant Integration | Apex AV Denver" },
      { name: "description", content: "Hands-free voice control for lighting, shades, audio, climate, and security across Denver homes — integrated with your smart home." },
      
      { name: "keywords", content: "voice control Denver, smart home voice control Denver, voice activated lighting Denver, hands-free home automation Denver, smart speaker installation Denver" },
      { property: "og:title", content: "Voice Control — Apex AV Denver" },
      { property: "og:description", content: "Hands-free voice control for lights, shades, audio, climate, and security across Denver homes." },
      { property: "og:image", content: abs(img) },
      { name: "twitter:image", content: abs(img) },
      { property: "og:url", content: "https://apexav.com/services/voice-control" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Voice Control — Apex AV Denver" },
      { name: "twitter:description", content: "Hands-free voice control for lights, shades, audio, climate, and security across Denver homes." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/voice-control" }, { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "voice-control",
            name: "Voice Control & Smart Assistant Integration",
            description: "Hands-free voice control for lighting, shades, audio, climate, and security in Denver homes.",
            pageTitle: "Voice Control & Smart Assistant Integration | Apex AV Denver",
            pageDescription: "Hands-free voice control for lighting, shades, audio, climate, and security across Denver homes — integrated with your smart home.",
            image: img,
            faqs: serviceFaqs["voice-control"],
            serviceType: "Voice Control & Smart Assistant Integration",
            breadcrumbName: "Voice Control Denver",
            offerCatalog: bullets,
          })
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      topics={["smart-home", "lighting", "networking"]}
      eyebrow="Voice control"
      title="Just say the word."
      intro={<>Native <strong>voice control in Denver</strong> homes — leading voice assistants and privacy-first local options integrated with your <strong>smart home automation</strong>, lights, shades, audio, climate, and security.</>}
      image={img}
      imageAlt="Voice control and smart assistant integration in a Parker Denver smart home"
      bullets={bullets}
      features={[
        { title: "One command, whole house", desc: "Say 'goodnight' and watch lights dim, shades close, doors lock, and alarm arm." },
        { title: "Privacy-first option", desc: "Local processing available — nothing sent to the cloud, no ads, no listening." },
        { title: "Hear you anywhere", desc: "Mic coverage tuned room-by-room so you don't have to shout from the kitchen." },
      ]}
      faqs={serviceFaqs["voice-control"]}
    />
  ),
});
